import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Section sub-navigation pill bar for consolidated admin areas
 * (Cleaners, Invoices, User Management, Analytics, Schedule & Jobs).
 * Matches the existing dark zinc/orange admin aesthetic.
 *
 * Usage:
 *   <AdminSubNav
 *     section="Cleaners"
 *     items={[
 *       { label: 'Cleaners', to: '/admin/cleaners' },
 *       { label: 'Performance', to: '/admin/performance' },
 *       { label: 'Payouts', to: '/admin/payouts' },
 *     ]}
 *   />
 */
export const AdminSubNav = ({ section = '', items, sticky = true }) => {
  const { pathname } = useLocation();
  const { isMasterAdmin } = useAuth();
  const safeSection = section || '';
  const visible = items.filter((it) => !it.masterOnly || isMasterAdmin);

  return (
    <nav
      className={`bg-[#f4f2ed] border-b border-[#e3e0d6] ${sticky ? 'sticky top-16 z-30' : ''}`}
      data-testid={`subnav-${(safeSection || 'section').toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-1.5 overflow-x-auto" role="tablist">
        {safeSection && (
          <span className="text-[9px] font-bold text-[#7a7c6d] uppercase tracking-[0.16em] mr-3 whitespace-nowrap hidden sm:inline">
            {safeSection}
          </span>
        )}
        {visible.map((item) => {
          const isActive = pathname === item.to || (item.activePrefix && pathname.startsWith(item.activePrefix));
          return (
            <Link
              key={item.to}
              to={item.to}
              role="tab"
              aria-selected={isActive}
              data-testid={`subnav-link-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              className={`px-3.5 py-2.5 rounded-[5px] text-[13px] whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-[#252a20] text-white'
                  : 'text-[#5f6455] hover:bg-[#ebe8df]'
              }`}
            >
              {item.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// Pre-defined item lists for the 5 consolidated admin sections
export const SUBNAV_CLEANERS = [
  { label: 'Gurus', to: '/admin/cleaners', activePrefix: '/admin/cleaners/' },
  { label: 'Hiring', to: '/admin/hiring', activePrefix: '/admin/hiring/' },
  { label: 'Time Tracking', to: '/admin/time-tracking' },
  { label: 'Quality', to: '/admin/qc-templates' },
  { label: 'Performance', to: '/admin/performance' },
  { label: 'Payouts', to: '/admin/payouts' },
];

export const SUBNAV_INVOICES = [
  { label: 'Overview', to: '/admin/billing' },
  { label: 'Invoices', to: '/admin/invoices' },
  { label: 'Payments', to: '/admin/billing/payments' },
  { label: 'Recurring', to: '/admin/recurring-invoices' },
  { label: 'Card Links', to: '/admin/sent-links' },
];

// Settings section: existing configuration pages, each keeping its own route and permissions.
export const SUBNAV_SETTINGS = [
  { label: 'Business', to: '/admin/settings', activePrefix: '/admin/settings/' },
  { label: 'Services', to: '/admin/services' },
  { label: 'Service Areas', to: '/admin/service-areas' },
  { label: 'Roles & Access', to: '/admin/users', activePrefix: '/admin/users/' },
  { label: 'Audit Log', to: '/admin/audit-log' },
];
export const SUBNAV_USER_MGMT = SUBNAV_SETTINGS;

export const SUBNAV_ANALYTICS = [
  { label: 'Overview', to: '/admin/analytics' },
  { label: 'Expenses', to: '/admin/analytics/expenses' },
  { label: 'Cash Flow', to: '/admin/analytics/cash-flow' },
  { label: 'Service Performance', to: '/admin/analytics/services' },
  { label: 'Data Checks', to: '/admin/analytics/data-checks' },
  { label: 'Performance Reports', to: '/admin/reports' },
];

export const SUBNAV_SCHEDULE = [
  { label: 'Calendar', to: '/admin/calendar' },
  { label: 'My Day', to: '/admin/schedule/day' },
  { label: 'Appointments', to: '/admin/appointments' },
  { label: 'Change History', to: '/admin/schedule/changes' },
];

export const SUBNAV_CONTENT = [
  { label: 'Reviews', to: '/admin/reviews' },
  { label: 'FAQs', to: '/admin/faqs' },
];

export default AdminSubNav;
